<template>
  <div class="attendee-modal-mobile-menu">
    <nav>
      <ul>
        <li
          v-for="item in visibleNavigation"
          :key="item.path"
          class="attendee-modal-mobile-menu__item"
          :class="{
            active:
              ($route.path.includes('agenda') && agendaRoute === item.path) ||
              ($route.path.includes('ce-info') && ceRoute === item.path),
          }"
        >
          <router-link :to="item.path">
            <span>
              <i class="fa-regular" :class="item.icon"></i>
            </span>
            <div class="attendee-modal-mobile-menu__text">
              <div class="attendee-modal-mobile-menu__title">
                {{ item.name }}
              </div>
              <div class="attendee-modal-mobile-menu__description">
                {{ item.description }}
              </div>
            </div>
            <i class="fa-solid fa-chevron-right"></i>
          </router-link>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import common from "../../helpers/common";
import CommonMenu from "../../components/layout/CommonMenu";
import { ROUTES } from "../../router";
import touchDevice from "../../mixins/touch-device";

export default {
  mixins: [touchDevice],
  components: {
    CommonMenu,
  },
  computed: {
    ...mapGetters("attendeeStore", [
      "attendee",
      "showUpgradeTicketMenu",
      "isSingleSession",
      "sidebarMenu",
      "tabs",
      "showMySchedule",
      "hideCeInformation",
    ]),

    isMobile() {
      return this.windowWidth < 900;
    }
  },
  data() {
    return {
      windowWidth: window.innerWidth,
      agendaRoute: ROUTES.AGENDA_EVENT.replace(":attendeeId", this.$route.params.attendeeId),
      ceRoute: ROUTES.CE_INFO.replace(":attendeeId", this.$route.params.attendeeId),
      navigation: [],
    };
  },
  watch: {
    sidebarMenu: {
      handler(newVal) {
        if (newVal) {
          this.buildNav();
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods:{
    buildNav(){
      this.navigation = [];

      if (this.isSingleSession) {
        this.navigation.push({
          name: "Event",
          icon: "fa-calendar-days",
          path: ROUTES.SINGLE_EVENT.replace(":attendeeId", this.$route.params.attendeeId),
          description: this.isMobile ? "" : "Learn about our event",
        });
      } else {
        this.navigation.push({
          name: "Schedule",
          icon: `fa-calendar-check`,
          path: ROUTES.AGENDA_EVENT.replace(":attendeeId", this.$route.params.attendeeId),
          description: this.isMobile ? "" : "View session list",
        });
      }
      // scan in/out
      if (this.sidebarMenu.scan_in_out) {
        this.navigation.push({
          name: "Scan in/out",
          icon: "fa-qrcode",
          path: ROUTES.SCAN_IN_OUT.replace(":attendeeId", this.$route.params.attendeeId),
          description: this.isMobile ? "" : "Scan the room QR code to sign yourself in and out",
          touchOnly: true,
        });
      }
      // speakers
      if (this.sidebarMenu.speakers && !this.isSingleSession) {
        this.navigation.push({
          name: "Speakers",
          icon: "fa-user",
          path: ROUTES.SPEAKERS.replace(":attendeeId", this.$route.params.attendeeId),
          description: this.isMobile ? "" : "Browse speakers and their sessions",
        });
      }
      // ce info/ce's
      if (this.isSingleSession) {
        this.navigation.push({
          name: this.hideCeInformation ? "Info" : "CE Info",
          icon: "fa-briefcase",
          path: ROUTES.CE_INFO.replace(":attendeeId", this.$route.params.attendeeId),
          description: this.isMobile ? "" : (this.hideCeInformation ? "View event and access your certificate" : "View event and access your CE certificate"),
        });
      } else {
        this.navigation.push({
          name: this.hideCeInformation ? "Info" : "CEs",
          icon: "fa-circle-info",
          path: ROUTES.CE_INFO.replace(":attendeeId", this.$route.params.attendeeId),
          description: this.isMobile ? "" : (this.hideCeInformation ? "View event and access your certificate" : "View event and access your CE certificate"),
        });
      }

      // exhibit hall
      if (this.sidebarMenu.exhibit_hall) {
        this.navigation.push({
          name: "Exhibit Hall",
          icon: "fa-briefcase",
          path: ROUTES.EXHIBIT_HALL.replace(":attendeeId", this.$route.params.attendeeId),
          description: this.isMobile ? "" : "Learn about our exhibit hall",
        });
      }

      if (this.sidebarMenu.materials) {
        this.navigation.push({
          name: "Materials",
          icon: "fa-cube",
          path: ROUTES.EVENT_MATERIALS.replace(":attendeeId", this.$route.params.attendeeId),
          description: this.isMobile ? "" : "Access event content",
        });
      }

      if (this.showUpgradeTicketMenu) {
        this.navigation.push({
          name: "Upgrade Ticket",
          icon: "fa-arrow-up-from-bracket",
          path: ROUTES.UPGRADE_TICKET.replace(":attendeeId", this.$route.params.attendeeId),
          description: this.isMobile ? "" : "Upgrade your ticket to access more content",
        });
      }
    },
  },

  mounted() {
    common.setHeaderText("Home");
  },
};
</script>

<style lang="scss">
.menu-count {
  background-color: rgb(255, 102, 102);
  font-size: 12px;
  height: 15px;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 15px;
  border-radius: 15px;
  padding: 0 4px;
}

.attendee-modal-mobile-menu {
  width: 100%;
  padding: var(--attendee-gap-md);

  &__text {
  }

  &__title {
    color: var(--attendee-text-primary-color);
    font-weight: 500;
    font-size: 18px;
  }

  &__description {
    color: rgb(189, 187, 187);
    font-size: 14px;
  }

  &__item {
    padding: var(--attendee-gap-md);
    background-color: rgba(27, 217, 210, 0.01);
    border-radius: var(--attendee-card-border-radius);
    margin-bottom: var(--attendee-gap-xs);
    border: 1px solid var(--attendee-text-secondary-color);

    a {
      display: grid;
      grid-template-columns: 22px 1fr 20px;
      align-items: flex-start;
      grid-gap: 10px;

      span {
        svg {
          padding-top: 3px;
          color: var(--attendee-text-primary-color);
          font-size: 18px;
        }
      }

      svg {
        align-self: center;
        color: rgb(189, 187, 187);
      }
    }
  }
}
</style>
